/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  createContext, useEffect, useMemo, useState,
} from "react";
import * as Sentry from "@sentry/react";
import StepsProgressIndicator from "widgets/Onboarding/components/StepsProgressIndicator";
import ModalLogin from "components/ModalLogin";
import { trackPushState } from "utils/trackPushstate";
import { IconUILightbulb } from "components/Icon/UI";
import _ from "lodash";
import { useSearchParams } from "react-router-dom";
import getUrlParameter from "helpers/getUrlParameter";
import useTracking from "./hooks/useTracking";
import { getStepsToComplete } from "./utils/stepManager";
import RouteGenerator from "./components/RouteGenerator/RouteGenerator";
import { processCampaignForm } from "./utils/formManager";
import useUserExists from "./hooks/useUserExists";
import useUpdateOnboardingStore from "./hooks/useUpdateOnboardingStore";
import useOnboardUser from "./hooks/useOnboardUser";
import useGetStripeConnectUrl from "./hooks/useGetStripeConnectUrl";
import Guidance from "./components/Guidance/Guidance";
import "react-toastify/dist/ReactToastify.min.css";

import "./style.scss";
import useAuth from "./hooks/useAuth";
import useBreakpoint from "./hooks/useBreakpoint";
import ScrollToTop from "./components/ScrollToTop";
import Toaster from "./components/Toaster/Toaster";

export const StepContext = createContext();

const Onboarding = ({ loading, steps, stepList }) => {
  const [nextStep, setNextStep] = useState();
  const [completedSteps, setCompletedSteps] = useState([]);
  const [stepsToComplete, setStepsToComplete] = useState("");
  const [pathSubmitted, setPathSubmitted] = useState("");
  const [canProceedToNextStep, setCanProceedToNextStep] = useState(true);
  const [formIsValid, setFormIsValid] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [hideBadge, setHideBadge] = useState(false);
  const [captchaToken, setCaptchaToken] = useState({ token: null });

  const [guidanceOpen, setGuidanceOpen] = useState(true);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [requiresLogin, setRequiresLogin] = useState(false);
  const { getUserExists } = useUserExists();
  const [queryParameters] = useSearchParams();

  const { updateStore, updateLocalStorage, formData } = useUpdateOnboardingStore();
  const { user: authUser } = useAuth();
  const { track } = useTracking();
  const { size } = useBreakpoint();
  const stepNames = useMemo(
    () => steps.map((step) => _.toLower(_.replace(step.path, "/", ""))),
    [steps],
  );
  useMemo(() => {
    setGuidanceOpen(size >= 1120);
  }, [size]);

  useEffect(() => {
    // initialise onboarding on first step
    setNextStep(steps[0].path);
  }, []);

  useEffect(() => {
    setUser(authUser);
  }, [authUser]);

  const { getCampaignConnectUrl, connectUrl } = useGetStripeConnectUrl();
  useEffect(() => {
    if (connectUrl) {
      window.location.href = connectUrl;
    }
  }, [connectUrl]);

  const {
    onBoardUser,
    success: onBoardSuccess,
    campaignId,
    errors: onBoardErrors,
  } = useOnboardUser();

  useEffect(() => {
    if (onBoardErrors && onBoardErrors.length > 0) {
      Sentry.captureException(new Error("Error creating campaign"), {
        contexts: {
          errors: onBoardErrors,
          form_data: formData,
          current_step: nextStep,
          completed_steps: completedSteps,
          steps_to_complete: stepsToComplete,
        },
      });
      setError(onBoardErrors);
    }
  }, [onBoardErrors]);

  const handleSubmit = (data, formStepIsValid, formStepHasErrors, path) => {
    setModalVisible(false);
    setCanProceedToNextStep(false);
    setFormIsValid(formStepIsValid);
    updateStore(data);
    updateLocalStorage(data);
    setPathSubmitted(path);

    if (!user) {
      const email = Object.keys(data).includes("email");

      if (email && data.email !== "" && !user) {
        getUserExists(data.email).then((resolve) => {
          const userExists = resolve.data.userExists.ok;
          if (userExists) {
            setRequiresLogin(true);
          } else {
            setRequiresLogin(false);
            setCanProceedToNextStep(true);
          }
        });
      } else {
        setCanProceedToNextStep(true);
      }
    } else {
      setCanProceedToNextStep(true);
    }
  };

  useEffect(() => {
    if (canProceedToNextStep && pathSubmitted) {
      if (pathSubmitted.indexOf("step-2") > -1 || pathSubmitted.indexOf("you") > -1) {
        const data = {};
        formData.fields.forEach((row) => {
          data[row.name] = row.value;
        });
        const gclid = getUrlParameter("gid") || null;
        track(
          "OnboardingStarted",
          { email: data.email },
          {
            title: data.campaign_title,
            target: data.amount,
            step: pathSubmitted,
            gclid,
          },
        );
      }

      setCompletedSteps((prevState) => [...new Set([...prevState, pathSubmitted])]);

      // navigate to next step, except payment (since last step) and story (since managed by onBoardSuccess)
      if (!pathSubmitted.includes("payment") && !pathSubmitted.includes("story")) {
        const currentStepIndex = steps.findIndex((step) => step.path === pathSubmitted);
        setNextStep(steps[currentStepIndex + 1].path);
      }
    }
  }, [canProceedToNextStep, pathSubmitted]);

  useEffect(() => {
    if (requiresLogin && !user && !modalVisible) {
      setModalVisible(true);
    }
  }, [requiresLogin, modalVisible]);

  useEffect(() => {
    setStepsToComplete(getStepsToComplete(completedSteps, steps));
  }, [completedSteps, steps]);

  useEffect(() => {
    if (onBoardSuccess) {
      const currentStepIndex = steps.findIndex((step) => step.path === pathSubmitted);
      setNextStep(steps[currentStepIndex + 1].path);
    }
  }, [onBoardSuccess]);

  useEffect(() => {
    if (completedSteps.length > 0 && stepsToComplete.length === 1 && !onBoardSuccess) {
      setHideBadge(true);
      processCampaignForm(formData).then((validatedFormData) => {
        const { formDataValid, error } = validatedFormData;
        if (!formDataValid || error) {
          setError(error);
        } else {
          onBoardUser(validatedFormData);
        }
      });
    }
  }, [completedSteps, stepsToComplete, formData, formIsValid]);

  useEffect(() => {
    if (completedSteps.length > 0 && stepsToComplete.length === 0 && onBoardSuccess) {
      getCampaignConnectUrl(campaignId);
    }
  }, [completedSteps, stepsToComplete, campaignId, onBoardSuccess]);

  const currentStepConfig = steps.filter((step) => window.location.pathname.includes(step.path));

  const toggleGuidanceModal = () => {
    setGuidanceOpen(!guidanceOpen);
  };

  const handleNavigateBack = (newPath) => {
    setNextStep(newPath);
  };

  const handleModalClose = (loggedInUser) => {
    if (loggedInUser) {
      setUser(loggedInUser);
    }
    // reset login
    setRequiresLogin(false);
  };

  trackPushState(["/onboarding/beneficiary"]);

  useEffect(() => {
    const queryParamsSteps = [];
    const queryParamsData = {};

    queryParameters.forEach((value, query) => {
      if (_.indexOf(stepNames, query) >= 0) {
        queryParamsSteps.push(`/${query}`);
        queryParamsData[`${query}`] = +value;
      }
    });

    setCompletedSteps((prevState) => [...new Set([...prevState, ...queryParamsSteps])]);
  }, [stepNames]);

  if (loading) {
    return null;
  }

  return (
    <div className="onboarding-wrapper">
      <div className="onboarding">
        <ScrollToTop />
        <Toaster message={error} type="error" />
        {modalVisible && !user && (
          <ModalLogin
            title="Please log in to continue"
            classes="onboarding"
            onModalClose={handleModalClose}
            openModal={modalVisible}
            captchaToken={captchaToken.token}
            setCaptchaToken={setCaptchaToken}
          />
        )}
        <div className={`badge ${hideBadge ? "hide" : ""}`} onClick={toggleGuidanceModal}>
          <span className="help-icon --small">
            <IconUILightbulb fillRule="evenodd" viewBox="0 0 100 100" />
          </span>
        </div>

        <StepsProgressIndicator
          currentStep={currentStepConfig}
          routes={stepList}
          completedSteps={completedSteps}
          setNextStep={setNextStep}
          handleNavigateBack={handleNavigateBack}
        />
        <StepContext.Provider value={steps}>
          <RouteGenerator
            routes={stepList}
            navigateToRoute={nextStep}
            handleSubmit={handleSubmit}
          />
        </StepContext.Provider>
      </div>
      <Guidance
        isOpen={guidanceOpen}
        handleClose={toggleGuidanceModal}
        currentStep={currentStepConfig}
      />
    </div>
  );
};
export default Onboarding;
